import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
    ArrowsClockwise,
    CheckCircle,
    WarningCircle,
    Lightning,
    Wallet,
} from "@phosphor-icons/react";

function fmtAgo(iso) {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return "just now";
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

export default function AiStatusPanel() {
    const [status, setStatus] = useState(null);
    const [err, setErr] = useState("");
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const load = async () => {
        setErr("");
        try {
            const { data } = await api.get("/admin/ai-status");
            setStatus(data);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await api.get("/admin/ai-status");
                if (!cancelled) setStatus(data);
            } catch (err) {
                if (!cancelled) console.debug("ai-status poll failed:", err?.message || err);
            }
        };
        tick();
        const t = setInterval(tick, 8000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, []);

    const onTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post("/admin/ai-status/test");
            setTestResult(data);
            await load();
        } catch (e) {
            setTestResult({ ok: false, error: formatApiError(e) });
        } finally {
            setTesting(false);
        }
    };

    if (err) {
        return <div className="block-risk" data-testid="ai-status-error">{err}</div>;
    }
    if (!status) {
        return <div className="text-[#c2c2c2]" data-testid="ai-status-loading">Loading…</div>;
    }

    const c = status.counts_last_30 || {};
    const successRate = status.success_rate_pct;
    const ratePill =
        successRate == null
            ? null
            : successRate >= 95
                ? { label: "Healthy", c: "#2ec5b6" }
                : successRate >= 70
                    ? { label: "Degraded", c: "#d9a441" }
                    : { label: "At risk", c: "#ff6b81" };

    return (
        <div className="space-y-8" data-testid="ai-status-panel">
            {/* Top-level cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="border border-[#2a2a2a] bg-[#141414] p-5" data-testid="ai-status-card-rate">
                    <div className="overline mb-2">Success rate</div>
                    <div className="font-mono text-4xl">
                        {successRate == null ? "—" : `${successRate}%`}
                    </div>
                    {ratePill && (
                        <div
                            className="inline-block mt-3 px-2 py-0.5 text-[11px] uppercase tracking-widest font-semibold"
                            style={{
                                color: ratePill.c,
                                borderTop: `1px solid ${ratePill.c}`,
                                borderBottom: `1px solid ${ratePill.c}`,
                            }}
                        >
                            ● {ratePill.label}
                        </div>
                    )}
                    <div className="text-[12px] text-[#9a9a9a] mt-3">last 30 calls</div>
                </div>
                <div className="border border-[#2a2a2a] bg-[#141414] p-5">
                    <div className="overline mb-2">Successes</div>
                    <div className="font-mono text-4xl text-[#2ec5b6]">{c.success || 0}</div>
                    <div className="text-[12px] text-[#9a9a9a] mt-3">
                        last: {fmtAgo(status.last_success?.ts)}
                    </div>
                </div>
                <div className="border border-[#2a2a2a] bg-[#141414] p-5">
                    <div className="overline mb-2">Retries</div>
                    <div className="font-mono text-4xl text-[#d9a441]">{c.retry || 0}</div>
                    <div className="text-[12px] text-[#9a9a9a] mt-3">
                        transient errors auto-handled
                    </div>
                </div>
                <div className="border border-[#2a2a2a] bg-[#141414] p-5">
                    <div className="overline mb-2">Failures</div>
                    <div className="font-mono text-4xl text-[#ff6b81]">{c.failed || 0}</div>
                    <div className="text-[12px] text-[#9a9a9a] mt-3">
                        last: {fmtAgo(status.last_failure?.ts)}
                    </div>
                </div>
            </div>

            {/* Test now + balance reminder */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-[#2a2a2a] bg-[#0a0a0a] p-5">
                    <div className="overline mb-2 flex items-center gap-2">
                        <Lightning size={14} weight="bold" /> Test now
                    </div>
                    <p className="text-[13px] text-[#c2c2c2] mb-3 leading-relaxed">
                        Fires a 1-word Claude call right now to verify your Universal Key
                        is funded and the LLM is responsive.
                    </p>
                    <button
                        type="button"
                        className="btn-primary inline-flex items-center gap-2"
                        onClick={onTest}
                        disabled={testing}
                        data-testid="ai-status-test-button"
                    >
                        <ArrowsClockwise
                            size={14}
                            weight="bold"
                            className={testing ? "animate-spin" : ""}
                        />
                        {testing ? "Calling Claude…" : "Run health check"}
                    </button>
                    {testResult && (
                        <div
                            className={`mt-3 p-3 text-[13px] ${testResult.ok ? "block-opportunity" : "block-risk"}`}
                            data-testid="ai-status-test-result"
                        >
                            {testResult.ok ? (
                                <>
                                    <CheckCircle size={14} weight="bold" className="inline mr-1.5 -mt-0.5" />
                                    Reply: <span className="font-mono">{testResult.reply}</span> ·{" "}
                                    {testResult.latency_ms} ms
                                </>
                            ) : (
                                <>
                                    <WarningCircle size={14} weight="bold" className="inline mr-1.5 -mt-0.5" />
                                    {testResult.error}
                                </>
                            )}
                        </div>
                    )}
                </div>
                <div className="border border-[#d9a441]/50 bg-[#1a1410] p-5">
                    <div className="overline mb-2 flex items-center gap-2 !text-[#e6b863]">
                        <Wallet size={14} weight="bold" /> Universal Key balance
                    </div>
                    <p className="text-[13px] text-[#c2c2c2] mb-3 leading-relaxed">
                        Emergent doesn&apos;t expose live balance to apps. To check or top up:
                    </p>
                    <ol className="text-[13px] text-[#d4d4d4] list-decimal list-inside space-y-1">
                        <li>Open the Emergent platform (not this app).</li>
                        <li>
                            Click <strong>Profile</strong> → <strong>Universal Key</strong>.
                        </li>
                        <li>
                            Hit <strong>Add Balance</strong> or toggle <strong>Auto top-up</strong>.
                        </li>
                    </ol>
                    <p className="text-[12px] text-[#9a9a9a] mt-3 leading-relaxed">
                        Suggested: auto top-up <strong className="text-white">30–50 ECU</strong>{" "}
                        for a steady production tool.
                    </p>
                </div>
            </div>

            {/* Recent calls */}
            <div>
                <div className="overline mb-3">Recent call log (last {status.log?.length || 0})</div>
                {(!status.log || status.log.length === 0) ? (
                    <div className="border border-dashed border-[#2a2a2a] p-6 text-[#9a9a9a] text-sm" data-testid="ai-status-log-empty">
                        No AI calls logged yet. Run a health check above, or let a real
                        assessment generation populate this list.
                    </div>
                ) : (
                    <div className="border border-[#2a2a2a] divide-y divide-[#2a2a2a]" data-testid="ai-status-log">
                        {status.log.map((row, idx) => {
                            const color =
                                row.status === "success"
                                    ? "#2ec5b6"
                                    : row.status === "retry"
                                        ? "#d9a441"
                                        : "#ff6b81";
                            const rowKey = `${row.ts || "log"}-${idx}-${row.status || ""}`;
                            return (
                                <div
                                    key={rowKey}
                                    className="p-3 bg-[#0a0a0a] grid grid-cols-12 gap-2 text-[12px] items-center"
                                    data-testid={`ai-status-log-row-${idx}`}
                                >
                                    <div className="col-span-2 font-mono" style={{ color }}>
                                        ● {row.status.toUpperCase()}
                                    </div>
                                    <div className="col-span-2 text-[#9a9a9a]">
                                        {fmtAgo(row.ts)}
                                    </div>
                                    <div className="col-span-2 text-[#c2c2c2]">
                                        {row.purpose || "—"}
                                        {row.attempt > 1 && (
                                            <span className="text-[#9a9a9a]">
                                                {" "}· attempt {row.attempt}/{row.max_attempts}
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-span-2 font-mono text-[#d4d4d4]">
                                        {row.latency_ms != null ? `${row.latency_ms} ms` : "—"}
                                    </div>
                                    <div className="col-span-4 truncate text-[#9a9a9a]">
                                        {row.error || row.assessment_id || ""}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="text-[11px] text-[#7a7a7a] mt-2 leading-relaxed">
                    Status auto-refreshes every 8 seconds. Log is capped at the 50 most
                    recent attempts.
                </div>
            </div>
        </div>
    );
}
